<template>
  <div
    :class="$style.component"
    ref="componentRef"
  >
    <button ref="buttonRef" v-on="$listeners">
      <slot />
    </button>
    <div
      v-if="errorLabel"
    >
      <div
        ref="labelRef"
        :class="$style.errorLabel"
      >
        {{errorLabel}}
      </div>
    </div>
  </div>
</template>

<script>
import {ref, watch} from '@vue/composition-api'

import useTimeouts from '@/composition/useTimeouts'
import useZoom from '@/composition/useZoom'

export default {
  props: {
    position: {type: Array, required: false},
    size: {type: Array, required: false},
    zIndex: {type: Number, required: false},
    boardRef: {required: true},
    errorLabel: {type: String},
  },
  setup (props, context) {
    const componentRef = ref()
    const buttonRef = ref()
    const labelRef = ref()
    const setTimeout = useTimeouts()
    useZoom(buttonRef)
    useZoom(labelRef)

    if (props.position) {
      setPosition(props.position[0], props.position[1])
    }

    watch(() => [props.position, componentRef.value], () => {
      if (componentRef.value && props.position) {
        // Use timeout to work around an issue where the item size is not correct on first load
        setTimeout(() => {
          setPosition(props.position[0], props.position[1])
          keepInBounds()
        })
      }
    }, {
      immediate: true,
    })

    return {
      componentRef,
      buttonRef,
      labelRef,
    }

    function setPosition (x, y) {
      if (!componentRef.value) {
        return
      }

      componentRef.value.style.left = `${x || 0}px`
      componentRef.value.style.top = `${y || 0}px`
    }

    function keepInBounds () {
      const {
        x: itemX,
        y: itemY,
      } = getItemPosition()
      const {
        width: boardWidth,
        height: boardHeight,
      } = getBoardDimensions()
      const {
        width: itemWidth,
        height: itemHeight,
      } = getItemDimensions()

      let newX = itemX
      let newY = itemY

      const minX = itemWidth / 2
      const minY = itemHeight / 2
      const maxX = boardWidth - itemWidth / 2
      const maxY = boardHeight - itemHeight / 2

      if (itemX < minX) newX = minX
      if (itemY < minY) newY = minY
      if (itemX > maxX) newX = maxX
      if (itemY > maxY) newY = maxY

      if (newX !== itemX || newY !== itemY) {
        setPosition(newX, newY)
      }
    }

    function getItemDimensions () {
      const {
        width,
        height,
      } = componentRef.value.getBoundingClientRect()
      return {width, height}
    }

    function getItemPosition () {
      const {
        offsetLeft: x,
        offsetTop: y,
      } = componentRef.value
      return {x, y}
    }

    function getBoardDimensions () {
      const {
        width,
        height,
      } = props.boardRef.getBoundingClientRect()
      return {width, height}
    }
  },
}
</script>

<style module lang="scss">
.component {
  position: absolute;
  user-select: none;
  z-index: 999;

  > * {
    transform: translate(-50%, -50%);
  }
}

.errorLabel {
  white-space: pre-wrap;
  position: absolute;
  bottom: 0;
  left: 0%;
  width: 100%;
  text-align: center;
  color: #fd341f;
  transform: translateY(50%);

  @include mixins.m-body-size;
}
</style>
